'use client';

export interface AnalyticsAttributionContext {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  utmId?: string;
  gclid?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  ttclid?: string;
  ttp?: string;
  msclkid?: string;
  gadSource?: string;
  wbraid?: string;
  gbraid?: string;
}

export type AnalyticsAttributionParamKey =
  | 'utm_source'
  | 'utm_medium'
  | 'utm_campaign'
  | 'utm_content'
  | 'utm_term'
  | 'utm_id'
  | 'gclid'
  | 'fbclid'
  | 'fbc'
  | 'fbp'
  | 'ttclid'
  | 'ttp'
  | 'msclkid'
  | 'gad_source'
  | 'wbraid'
  | 'gbraid';

export type AnalyticsAttributionParams = Partial<Record<AnalyticsAttributionParamKey, string>>;

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const STORAGE_KEY = 'ss_analytics_attribution';

const PARAM_TO_CONTEXT_KEY: Record<AnalyticsAttributionParamKey, keyof AnalyticsAttributionContext> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
  utm_id: 'utmId',
  gclid: 'gclid',
  fbclid: 'fbclid',
  fbc: 'fbc',
  fbp: 'fbp',
  ttclid: 'ttclid',
  ttp: 'ttp',
  msclkid: 'msclkid',
  gad_source: 'gadSource',
  wbraid: 'wbraid',
  gbraid: 'gbraid',
};

const ATTRIBUTION_PARAM_KEYS = Object.keys(PARAM_TO_CONTEXT_KEY) as AnalyticsAttributionParamKey[];

function cleanValue(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function readStoredAttribution(storage: StorageLike | undefined): AnalyticsAttributionContext {
  if (!storage) {
    return {};
  }
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const result: AnalyticsAttributionContext = {};

    for (const contextKey of Object.values(PARAM_TO_CONTEXT_KEY)) {
      const value = parsed[contextKey];
      if (typeof value === 'string' && value.trim().length > 0) {
        result[contextKey] = value.trim();
      }
    }
    return result;
  } catch {
    return {};
  }
}

function writeStoredAttribution(storage: StorageLike | undefined, attribution: AnalyticsAttributionContext): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Storage can be unavailable in private browsing or embedded contexts.
  }
}

function getDefaultStorage(): StorageLike | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function getDefaultCookieString(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  return document.cookie;
}

function parseCookieValue(cookieString: string | undefined, cookieName: string): string | undefined {
  if (!cookieString) {
    return undefined;
  }
  const match = cookieString
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${cookieName}=`));
  if (!match) {
    return undefined;
  }
  try {
    return decodeURIComponent(match.slice(cookieName.length + 1));
  } catch {
    return match.slice(cookieName.length + 1);
  }
}

function readVendorCookieAttribution(cookieString: string | undefined): AnalyticsAttributionContext {
  const attribution: AnalyticsAttributionContext = {};
  const fbc = cleanValue(parseCookieValue(cookieString, '_fbc'));
  const fbp = cleanValue(parseCookieValue(cookieString, '_fbp'));
  const ttp = cleanValue(parseCookieValue(cookieString, '_ttp'));
  if (fbc) attribution.fbc = fbc;
  if (fbp) attribution.fbp = fbp;
  if (ttp) attribution.ttp = ttp;
  return attribution;
}

function buildFbcFromFbclid(fbclid: string | undefined, nowMs: number): string | undefined {
  const cleaned = cleanValue(fbclid);
  if (!cleaned) {
    return undefined;
  }
  return `fb.1.${Math.trunc(nowMs)}.${cleaned}`;
}

export function mapAttributionParamsToAnalyticsContext(
  params: AnalyticsAttributionParams,
): AnalyticsAttributionContext {
  const result: AnalyticsAttributionContext = {};

  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const value = cleanValue(params[key]);
    if (!value) {
      continue;
    }
    result[PARAM_TO_CONTEXT_KEY[key]] = value;
  }

  return result;
}

export function parseAnalyticsAttributionFromUrl(url: string | undefined): AnalyticsAttributionContext {
  if (!url) {
    return {};
  }
  try {
    const parsed = new URL(url);
    const params: AnalyticsAttributionParams = {};
    for (const key of ATTRIBUTION_PARAM_KEYS) {
      const value = parsed.searchParams.get(key);
      if (value) {
        params[key] = value;
      }
    }
    return mapAttributionParamsToAnalyticsContext(params);
  } catch {
    return {};
  }
}

export function resolvePersistedAnalyticsAttribution(
  url: string | undefined,
  storage: StorageLike | undefined = getDefaultStorage(),
  cookieString: string | undefined = getDefaultCookieString(),
  nowMs: number = Date.now(),
): AnalyticsAttributionContext {
  const stored = readStoredAttribution(storage);
  const fromCookies = readVendorCookieAttribution(cookieString);
  const fromUrl = parseAnalyticsAttributionFromUrl(url);
  const merged = { ...stored, ...fromCookies, ...fromUrl };

  const generatedFbc =
    fromUrl.fbclid && !fromCookies.fbc && !fromUrl.fbc ? buildFbcFromFbclid(fromUrl.fbclid, nowMs) : undefined;
  if (generatedFbc) {
    merged.fbc = generatedFbc;
  }

  if (
    Object.keys(fromUrl).length > 0 ||
    Object.keys(fromCookies).some((key) => !!fromCookies[key as keyof AnalyticsAttributionContext]) ||
    generatedFbc
  ) {
    writeStoredAttribution(storage, merged);
  }

  return merged;
}
